import {
	Box,
	Button,
	Divider,
	Drawer,
	Group,
	NavLink,
	ScrollArea,
	Select,
	Stack,
	Text,
} from "@mantine/core";
import {useColorScheme} from "@mui/material";
import {
	IconDroplet,
	IconHome,
	IconKey,
	IconLeaf,
	IconLogout,
	IconMoon,
	IconShieldCheck,
	IconUser,
} from "@tabler/icons-react";
import {Link as RouterLink} from "react-router";
import {useAuth} from "@hooks/useAuth.tsx";
import logo from "/BalyGo-halloween.svg";
import logoInverted from "/BalyGo-inverted-halloween.svg";
import {usePopUp} from "@hooks/usePopUp.tsx";
import {NavItem} from "./AppHeader.tsx";
import {useCallback, useMemo} from "react";
import {menuItems} from "@components/default/MenuItems.tsx";

interface AppDrawerProps {
	open: boolean;
	onClose: () => void;
}

function CollapsibleNavItem({item, onClose}: { item: NavItem; onClose: () => void }) {
	const {permissions, hasPermission} = useAuth();

	const shouldHide = useCallback(
		({it}: { it: NavItem }) => {
			return (it.hide === "without permission" && !hasPermission(it.permissions)) || it.hide === true;
		},
		[permissions, hasPermission],
	);

	const hidden = useMemo(() => {
		return shouldHide({it: item});
	}, [item, shouldHide]);

	if (hidden) return null;

	const isDisabled =
		item.permissions != null &&
		!item.permissions.some((p) => permissions?.some((p2) => p.modulo === p2.modulo));

	if (item.children && item.children.length > 0) {
		return (
			<NavLink
				label={item.text}
				leftSection={item.icon}
				disabled={isDisabled}
				childrenOffset="md"
			>
				{item.children.map((child) => (
					<CollapsibleNavItem key={child.text} item={child} onClose={onClose} />
				))}
			</NavLink>
		);
	}

	return (
		<NavLink
			label={item.text}
			leftSection={item.icon}
			component={RouterLink}
			to={item.path!}
			onClick={onClose}
			disabled={isDisabled}
		/>
	);
}

export function AppDrawer({open, onClose}: AppDrawerProps) {
	const {isAuthenticated, user, logout, refreshPermissions} = useAuth();
	const {setMode, setColorScheme, colorScheme} = useColorScheme();
	const {addPopUp, closePopUp} = usePopUp();

	const isTropical = (colorScheme as string) === "tropical";
	const isIntencion = (colorScheme as string) === "intencion";

	return (
		<Drawer
			opened={open}
			onClose={onClose}
			position="left"
			size={320}
			padding="xs"
			withCloseButton={false}
		>
			<Group justify="space-between" align="center" px="xs" py="xs">
				<RouterLink to="/" onClick={onClose} style={{display: "flex", alignItems: "center"}}>
					<img
						src={isTropical ? logoInverted : logo}
						alt="Logo"
						style={{height: 45, maxWidth: 190, objectFit: "contain"}}
					/>
				</RouterLink>
				<Drawer.CloseButton onClick={onClose} />
			</Group>

			<Divider my="xs" />

			<ScrollArea h="calc(100vh - 75px)" offsetScrollbars>
				<Box px="xs" pb="xl">
					{/* Theme Select */}
					<Select
						label="Tema"
						size="sm"
						value={(colorScheme as string) ?? "tropical"}
						data={[
							{value: "tropical", label: "Energy tropical"},
							{value: "intencion", label: "Intencion"},
							{value: "masterGold", label: "Master Gold"},
						]}
						renderOption={({option}) => (
							<Group gap="xs">
								{option.value === "tropical" && <IconLeaf size={20} color="#ff9100" />}
								{option.value === "intencion" && <IconDroplet size={20} color="#1565C0" />}
								{option.value === "masterGold" && <IconMoon size={20} />}
								<span>{option.label}</span>
							</Group>
						)}
						leftSection={
							isTropical ? (
								<IconLeaf size={18} color="#ff9100" />
							) : isIntencion ? (
								<IconDroplet size={18} color="#1565C0" />
							) : (
								<IconMoon size={18} />
							)
						}
						onChange={(val) => {
							switch (val as "masterGold" | "intencion" | "tropical") {
								case "masterGold":
									setMode("dark");
									setColorScheme("masterGold" as any);
									break;
								case "intencion":
									setMode("light");
									setColorScheme("intencion" as any);
									break;
								case "tropical":
									setMode("light");
									setColorScheme("tropical" as any);
									break;
							}
						}}
						mb="sm"
					/>

					{/* Navigation Links */}
					<NavLink
						label="Início"
						leftSection={<IconHome size={18} />}
						component={RouterLink}
						to="/"
						onClick={onClose}
					/>

					{isAuthenticated &&
						menuItems.map((item) => (
							<CollapsibleNavItem key={item.text} item={item} onClose={onClose} />
						))}

					{isAuthenticated && (
						<>
							<Divider my="xs" />
							<NavLink
								disabled
								label={user?.nomeCompleto}
								description={`conectado como '${user?.userName}'`}
								leftSection={<IconUser size={18} />}
							/>
							<NavLink
								label="Desconectar"
								c="orange"
								leftSection={<IconLogout size={18} color="#ED6C02" />}
								onClick={() => {
									addPopUp({
										title: "Deseja desconectar?",
										content: (
											<Stack gap="md">
												<Text size="sm">Tem certeza que deseja desconectar?</Text>
												<Group justify="flex-end">
													<Button
														color="red"
														variant="filled"
														onClick={() => {
															logout();
															closePopUp();
														}}
													>
														Sim, Desconectar
													</Button>
												</Group>
											</Stack>
										),
									});
								}}
							/>
							<Divider my="xs" />
							<NavLink
								label="Atualizar permissões"
								c="blue"
								leftSection={<IconShieldCheck size={18} color="#42A5F5" />}
								onClick={() => {
									refreshPermissions().then();
								}}
							/>
						</>
					)}

					{!isAuthenticated && (
						<>
							<Divider my="xs" />
							<NavLink
								label="Login"
								c="green"
								leftSection={<IconKey size={18} color="#43A047" />}
								component={RouterLink}
								to="/login"
								onClick={onClose}
							/>
						</>
					)}
				</Box>
			</ScrollArea>
		</Drawer>
	);
}
